import React, { useState, useRef, useCallback, useEffect } from "react";
import cn from "classnames";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faUpload,
  faMagic,
  faTimes,
  faSpinner,
  faDownload,
  faPlus,
  faRotateRight,
  faImages,
  faCamera,
  faPaperPlane,
  faLightbulb,
  faCrop,
  faEye,
} from "@fortawesome/free-solid-svg-icons";
import { toast } from "react-toastify";
import Content from "../layout/Content";
import Layout from "../layout/Layout";
import socialApiClient from "../../cores/helpers/services/social-api-client";
import { rotateBlobWithCanvas } from "../../cores/helpers/rotate-image-blob";
import { fetchAiGeneratedImageBlob } from "../../cores/helpers/fetch-ai-generated-blob";
import { AiImageCropModal } from "../component/ai/AiImageCropModal";
import { AiImageViewerEditorModal } from "../component/ai/AiImageViewerEditorModal";
import {
  loadAiGenerationRotations,
  saveAiGenerationRotations,
} from "../../cores/helpers/text-to-image-rotation-storage";
import { useMeCore } from "../../cores/me";
import { useIntl } from "react-intl";
import { PostImage } from "../../cores/socialPost/interfaces/postImage";
import { useRouterCore } from "../../cores/router";
import { buildPostImageFromBlobForSocial } from "../../cores/helpers/social-post-image-from-blob";
import { storePendingSocialPostImages } from "../../cores/helpers/pending-social-post-image";
import { ImageBankModal } from "../component/socialPost/ImageBankModal";
import {
  AI_RESULTS_COLUMNS_STORAGE,
  loadAiResultsColumns,
  saveAiResultsColumns,
  type AiResultsColumns,
} from "../../cores/helpers/ai-results-grid-columns-storage";
import { takeAiEditorImportFromTextToImage } from "../../cores/helpers/ai-editor-import-from-text-to-image";
import { Header2 } from "../layout/Header2";
import { ProductGuideModal } from "../component/guides/ProductGuideModal";
import { AI_GUIDE_STEPS } from "../component/guides/productGuideConfigs";
import { useProductGuideQuery } from "../../hooks/useProductGuideQuery";
import { WEMBII_AI_CREDITS_REFRESH_EVENT } from "../../constants/dom.constant";

const GENERATE_ENDPOINT = "nanobanana/generate";
const GENERATE_OR_EDIT_ENDPOINT = "nanobanana/generate-or-edit";
const STATUS_ENDPOINT = "nanobanana/status";

interface UploadedImage {
  id: string;
  file: File;
  preview: string;
}

interface GeneratedImage {
  id: string;
  url: string;
  prompt: string;
  timestamp: number;
}

const AI_GENERATION_EMPTY_STEPS = [
  "app.aiGeneration.emptyStep1",
  "app.aiGeneration.emptyStep2",
  "app.aiGeneration.emptyStep3",
] as const;

export default function AIGeneration() {
  const { companyId } = useMeCore();
  const intl = useIntl();
  const { goToSocialPostFromAiImage } = useRouterCore();
  const { showGuide: showAiGuide, closeGuide: closeAiGuide } =
    useProductGuideQuery("ai");
  const [uploadedImages, setUploadedImages] = useState<UploadedImage[]>([]);
  const [editDescription, setEditDescription] = useState("");
  const [generatedImages, setGeneratedImages] = useState<GeneratedImage[]>([]);
  const [isGenerating, setIsGenerating] = useState(false);
  const [generationProgress, setGenerationProgress] = useState(0);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [currentTaskId, setCurrentTaskId] = useState<string | null>(null);
  const [taskStatus, setTaskStatus] = useState<string | null>(null);
  const [canCheckStatus, setCanCheckStatus] = useState(false);
  const [aspectRatio, setAspectRatio] = useState("4:5");
  const [resolution, setResolution] = useState("1K");
  const [apiMode, setApiMode] = useState<"pro" | "edit">("edit");
  const [callBackUrl, setCallBackUrl] = useState("");
  const [numImages, setNumImages] = useState(1);
  const [imageSize, setImageSize] = useState("4:5");
  const [watermark, setWatermark] = useState("");
  /** Rotation in degrees (0, 90, 180, 270) per generated image id (persistido en localStorage) */
  const [imageRotations, setImageRotations] = useState<Record<string, number>>({});
  /** Evita podar rotaciones antes de que el historial haya cargado */
  const [historyReady, setHistoryReady] = useState(false);
  const [showImageBankModal, setShowImageBankModal] = useState(false);
  const [postingToSocialId, setPostingToSocialId] = useState<string | null>(null);
  const [resultColumns, setResultColumns] = useState<AiResultsColumns>(() =>
    loadAiResultsColumns(AI_RESULTS_COLUMNS_STORAGE.aiGeneration, 3)
  );
  const [cropModal, setCropModal] = useState<{
    imageId: string;
    src: string;
  } | null>(null);
  const [cropApplying, setCropApplying] = useState(false);
  const [cropPreparingId, setCropPreparingId] = useState<string | null>(null);
  const [viewerModal, setViewerModal] = useState<{
    imageId: string;
    src: string;
  } | null>(null);
  const [viewerApplying, setViewerApplying] = useState(false);
  const [viewerPreparingId, setViewerPreparingId] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const cameraInputRef = useRef<HTMLInputElement>(null);
  const uploadedCountRef = useRef(0);
  const maxImages = 3;
  const maxFileSize = 10 * 1024 * 1024; // 10MB

  useEffect(() => {
    saveAiResultsColumns(AI_RESULTS_COLUMNS_STORAGE.aiGeneration, resultColumns);
  }, [resultColumns]);

  useEffect(() => {
    if (!companyId) {
      setHistoryReady(false);
      return;
    }

    setImageRotations(loadAiGenerationRotations(companyId));
    setHistoryReady(false);

    let cancelled = false;
    const loadHistory = async () => {
      try {
        const { data } = await socialApiClient().get("nanobanana/history", {
          params: { companyId, limit: 50 },
        });
        if (cancelled) return;
        const items = data?.payload || [];
        const mapped: GeneratedImage[] = items.map((item: any) => ({
          id: String(item.id),
          url: item.imageUrl,
          prompt: item.prompt || "",
          timestamp: new Date(item.createdAt).getTime() || Date.now(),
        }));
        setGeneratedImages(mapped);
      } catch {
        if (!cancelled) {
          setGeneratedImages([]);
        }
      } finally {
        if (!cancelled) {
          setHistoryReady(true);
        }
      }
    };

    loadHistory();
    return () => {
      cancelled = true;
    };
  }, [companyId]);

  /** Quitar rotaciones de imágenes que ya no están en el historial */
  useEffect(() => {
    if (!companyId || !historyReady) return;
    setImageRotations((prev) => {
      const ids = new Set(generatedImages.map((g) => g.id));
      const next: Record<string, number> = {};
      let changed = false;
      for (const [id, deg] of Object.entries(prev)) {
        if (ids.has(id)) next[id] = deg;
        else changed = true;
      }
      return changed ? next : prev;
    });
  }, [companyId, historyReady, generatedImages]);

  /** Persistir rotaciones de resultados (por empresa) */
  useEffect(() => {
    if (!companyId) return;
    saveAiGenerationRotations(companyId, imageRotations);
  }, [companyId, imageRotations]);

  /** Imagen enviada desde Text-to-image: cargar como referencia en el editor IA. */
  useEffect(() => {
    if (!companyId) return;
    const pending = takeAiEditorImportFromTextToImage();
    if (!pending) return;

    (async () => {
      try {
        if (uploadedCountRef.current >= maxImages) {
          toast.error(
            intl.formatMessage(
              { id: "app.aiGeneration.errMaxRefsExceeded" },
              { maxImages }
            )
          );
          return;
        }
        const { data, headers } = await socialApiClient().get(
          "nanobanana/download-image",
          {
            responseType: "blob",
            params: { imageUrl: pending.imageUrl, companyId },
          }
        );
        const rawBlob: Blob = data;
        const deg = pending.rotationDeg ?? 0;
        const blob: Blob =
          deg !== 0 ? await rotateBlobWithCanvas(rawBlob, deg) : rawBlob;
        const contentType =
          headers["content-type"] || blob.type || "image/jpeg";
        const ext = blob.type.includes("png")
          ? "png"
          : blob.type.includes("webp")
          ? "webp"
          : "jpg";
        const file = new File(
          [blob],
          `text-to-image-${Date.now()}.${ext}`,
          { type: contentType }
        );
        const newImage: UploadedImage = {
          id: `${Date.now()}-${Math.random()}`,
          file,
          preview: URL.createObjectURL(file),
        };
        setUploadedImages((prev) => [...prev, newImage].slice(0, maxImages));
        if (pending.prompt?.trim()) {
          setEditDescription(pending.prompt.trim());
        }
        setApiMode("edit");
        setErrorMessage(null);
        toast.success(
          intl.formatMessage({ id: "app.textToImage.editInEditorToast" })
        );
      } catch (e) {
        console.error("Import from text-to-image:", e);
        toast.error(
          intl.formatMessage({ id: "app.textToImage.editInEditorToastError" })
        );
      }
    })();
  }, [companyId, intl, maxImages]);

  useEffect(() => {
    uploadedCountRef.current = uploadedImages.length;
  }, [uploadedImages.length]);

  const handleFileSelect = useCallback((files: FileList | null) => {
    if (!files) return;

    const validFiles: File[] = [];
    let rejectedBySize = 0;
    let rejectedByType = 0;
    let rejectedByLimit = 0;
    Array.from(files).forEach((file) => {
      if (!file.type.startsWith("image/")) {
        rejectedByType += 1;
        return;
      }
      if (file.size > maxFileSize) {
        rejectedBySize += 1;
        return;
      }
      if (uploadedImages.length + validFiles.length >= maxImages) {
        rejectedByLimit += 1;
        return;
      }
      validFiles.push(file);
    });

    const newImages: UploadedImage[] = validFiles.map((file) => ({
      id: `${Date.now()}-${Math.random()}`,
      file,
      preview: URL.createObjectURL(file),
    }));

    setUploadedImages((prev) => [...prev, ...newImages].slice(0, maxImages));
    if (rejectedBySize > 0) {
      setErrorMessage(intl.formatMessage({ id: "app.aiGeneration.errFileSizeExceeded" }));
      return;
    }
    if (rejectedByType > 0) {
      setErrorMessage(intl.formatMessage({ id: "app.aiGeneration.errInvalidFileType" }));
      return;
    }
    if (rejectedByLimit > 0) {
      setErrorMessage(intl.formatMessage({ id: "app.aiGeneration.errMaxImagesExceeded" }, { maxImages }));
      return;
    }
    setErrorMessage(null);
  }, [uploadedImages.length]);

  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    handleFileSelect(e.target.files);
    if (e.target) e.target.value = "";
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    handleFileSelect(e.dataTransfer.files);
  };

  const removeImage = (id: string) => {
    setUploadedImages((prev) => {
      const image = prev.find((img) => img.id === id);
      if (image) {
        URL.revokeObjectURL(image.preview);
      }
      return prev.filter((img) => img.id !== id);
    });
  };

  /** Imágenes desde el banco Pexels (mismo modal que en publicaciones sociales) */
  const handleGalleryImagesSelect = useCallback(
    async (postImages: PostImage[]) => {
      setShowImageBankModal(false);
      const remainingSlots = maxImages - uploadedCountRef.current;
      if (remainingSlots <= 0 && postImages.length > 0) {
        setErrorMessage(intl.formatMessage({ id: "app.aiGeneration.errMaxImagesExceeded" }, { maxImages }));
        return;
      }

      const newItems: UploadedImage[] = [];
      let rejectedBySize = 0;

      for (const pi of postImages) {
        try {
          const res = await fetch(pi.data);
          const blob = await res.blob();
          const file = new File([blob], pi.name, {
            type: pi.type || blob.type || "image/jpeg",
          });
          if (!file.type.startsWith("image/")) {
            continue;
          }
          if (file.size > maxFileSize) {
            rejectedBySize += 1;
            continue;
          }
          newItems.push({
            id: `${Date.now()}-${Math.random()}`,
            file,
            preview: URL.createObjectURL(file),
          });
        } catch (e) {
          console.error("AI generation gallery import:", e);
        }
      }

      setUploadedImages((prev) => {
        const remaining = maxImages - prev.length;
        if (remaining <= 0) return prev;
        return [...prev, ...newItems.slice(0, remaining)];
      });

      if (rejectedBySize > 0) {
        setErrorMessage(intl.formatMessage({ id: "app.aiGeneration.errFileSizeExceeded" }));
      } else if (postImages.length > 0 && newItems.length === 0) {
        setErrorMessage(intl.formatMessage({ id: "app.aiGeneration.errGalleryImportFailed" }));
      } else {
        setErrorMessage(null);
      }
    },
    [intl, maxFileSize, maxImages]
  );

  const rotateGeneratedImage = (id: string) => {
    setImageRotations((prev) => ({
      ...prev,
      [id]: ((prev[id] ?? 0) + 90) % 360,
    }));
  };

  const closeCropModal = () => {
    setCropModal((prev) => {
      if (prev?.src) URL.revokeObjectURL(prev.src);
      return null;
    });
  };

  const openCropModalForImage = async (img: GeneratedImage) => {
    setCropPreparingId(img.id);
    try {
      const raw = await fetchAiGeneratedImageBlob(img.url, companyId);
      const deg = imageRotations[img.id] ?? 0;
      const blob: Blob =
        deg !== 0 ? await rotateBlobWithCanvas(raw, deg) : raw;
      const src = URL.createObjectURL(blob);
      setCropModal({ imageId: img.id, src });
    } catch {
      toast.error(
        intl.formatMessage({ id: "app.aiGeneration.cropOpenError" })
      );
    } finally {
      setCropPreparingId(null);
    }
  };

  const handleCropApply = async (blob: Blob) => {
    const modal = cropModal;
    if (!modal) return;
    setCropApplying(true);
    try {
      const newUrl = URL.createObjectURL(blob);
      setGeneratedImages((prev) =>
        prev.map((g) => {
          if (g.id !== modal.imageId) return g;
          if (g.url.startsWith("blob:")) URL.revokeObjectURL(g.url);
          return { ...g, url: newUrl };
        })
      );
      setImageRotations((prev) => {
        const next = { ...prev };
        delete next[modal.imageId];
        return next;
      });
      URL.revokeObjectURL(modal.src);
      setCropModal(null);
    } finally {
      setCropApplying(false);
    }
  };

  const closeViewerModal = () => {
    setViewerModal((prev) => {
      if (prev?.src) URL.revokeObjectURL(prev.src);
      return null;
    });
  };

  const openViewerForImage = async (img: GeneratedImage) => {
    setViewerPreparingId(img.id);
    try {
      const raw = await fetchAiGeneratedImageBlob(img.url, companyId);
      const deg = imageRotations[img.id] ?? 0;
      const blob: Blob =
        deg !== 0 ? await rotateBlobWithCanvas(raw, deg) : raw;
      const src = URL.createObjectURL(blob);
      setViewerModal({ imageId: img.id, src });
    } catch {
      toast.error(
        intl.formatMessage({ id: "app.aiGeneration.cropOpenError" })
      );
    } finally {
      setViewerPreparingId(null);
    }
  };

  const handleViewerApply = async (blob: Blob) => {
    const modal = viewerModal;
    if (!modal) return;
    setViewerApplying(true);
    try {
      const newUrl = URL.createObjectURL(blob);
      setGeneratedImages((prev) =>
        prev.map((g) => {
          if (g.id !== modal.imageId) return g;
          if (g.url.startsWith("blob:")) URL.revokeObjectURL(g.url);
          return { ...g, url: newUrl };
        })
      );
      setImageRotations((prev) => {
        const next = { ...prev };
        delete next[modal.imageId];
        return next;
      });
      URL.revokeObjectURL(modal.src);
      setViewerModal(null);
    } finally {
      setViewerApplying(false);
    }
  };

  const downloadGeneratedImage = async (image: GeneratedImage) => {
    try {
      const data = await fetchAiGeneratedImageBlob(image.url, companyId);
      const deg = imageRotations[image.id] ?? 0;
      const blob: Blob = deg !== 0 ? await rotateBlobWithCanvas(data, deg) : data;
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      const datePart = new Date(image.timestamp).toISOString().slice(0, 10);
      link.download = `${datePart}_Wembii-${image.id}.jpg`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (e) {
      setErrorMessage(intl.formatMessage({ id: "app.aiGeneration.errDownloadFailed" }));
    }
  };

  const removeGeneratedImage = (id: string) => {
    setGeneratedImages((prev) => {
      const found = prev.find((img) => img.id === id);
      if (found?.url.startsWith("blob:")) {
        URL.revokeObjectURL(found.url);
      }
      return prev.filter((img) => img.id !== id);
    });
    setImageRotations((prev) => {
      const next = { ...prev };
      delete next[id];
      return next;
    });
    socialApiClient()
      .delete(`nanobanana/history/${id}`, {
        params: { companyId },
      })
      .catch(() => {
        // Si falla, solo se queda borrada en el front; podríamos recargar historial si hace falta
      });
  };

  const postImageToSocial = async (image: GeneratedImage) => {
    if (!companyId) {
      toast.error(intl.formatMessage({ id: "app.aiGeneration.errCompanyRequired" }));
      return;
    }
    setPostingToSocialId(image.id);
    try {
      const data = await fetchAiGeneratedImageBlob(image.url, companyId);
      const deg = imageRotations[image.id] ?? 0;
      const blob: Blob =
        deg !== 0 ? await rotateBlobWithCanvas(data, deg) : data;
      const datePart = new Date(image.timestamp).toISOString().slice(0, 10);
      const postImage = await buildPostImageFromBlobForSocial(
        blob,
        `${datePart}_Wembii-${image.id}.jpg`
      );
      try {
        storePendingSocialPostImages([postImage]);
      } catch (e: any) {
        if (e?.name === "QuotaExceededError" || e?.code === 22) {
          toast.error(
            intl.formatMessage({ id: "app.aiGeneration.errPostToSocialStorage" })
          );
          return;
        }
        throw e;
      }
      goToSocialPostFromAiImage();
    } catch {
      toast.error(
        intl.formatMessage({ id: "app.aiGeneration.errPostToSocialFailed" })
      );
    } finally {
      setPostingToSocialId(null);
    }
  };

  const addGeneratedAsReference = async (image: GeneratedImage) => {
    try {
      if (uploadedImages.length >= maxImages) {
        setErrorMessage(intl.formatMessage({ id: "app.aiGeneration.errMaxRefsExceeded" }, { maxImages }));
        return;
      }
      const rawBlob = await fetchAiGeneratedImageBlob(image.url, companyId);
      const deg = imageRotations[image.id] ?? 0;
      const blob: Blob = deg !== 0 ? await rotateBlobWithCanvas(rawBlob, deg) : rawBlob;
      const contentType = blob.type || "image/jpeg";
      const ext = blob.type.includes("png")
        ? "png"
        : blob.type.includes("webp")
        ? "webp"
        : "jpg";
      const file = new File([blob], `referencia-${Date.now()}.${ext}`, {
        type: contentType,
      });
      const newImage: UploadedImage = {
        id: `${Date.now()}-${Math.random()}`,
        file,
        preview: URL.createObjectURL(file),
      };
      setUploadedImages((prev) => [...prev, newImage].slice(0, maxImages));
      setErrorMessage(null);
    } catch (error: any) {
      setErrorMessage(
        error?.message || intl.formatMessage({ id: "app.aiGeneration.errRefFailed" })
      );
    }
  };

  const fetchTaskStatus = async (taskId: string) => {
    if (!companyId) {
      throw new Error(intl.formatMessage({ id: "app.aiGeneration.errNoCompanyId" }));
    }
    const { data } = await socialApiClient().get(STATUS_ENDPOINT, {
      params: { taskId, companyId },
    });
    const taskPayload = data?.payload || data;
    const resultUrl =
      taskPayload?.resultImageUrl ||
      taskPayload?.raw?.response?.resultImageUrl ||
      taskPayload?.raw?.data?.response?.resultImageUrl;

    if (taskPayload?.status === "success" && resultUrl) {
      return { imageUrl: resultUrl, taskPayload };
    }

    if (taskPayload?.raw?.errorMessage) {
      throw new Error(taskPayload.raw.errorMessage);
    }

    return { imageUrl: null, taskPayload };
  };

  const pollTaskStatus = async (taskId: string) => {
    const maxAttempts = 30;
    const delayMs = 2000;

    for (let attempt = 1; attempt <= maxAttempts; attempt += 1) {
      setTaskStatus(`Consultando estado... (${attempt}/${maxAttempts})`);
      const progress = 70 + Math.round((attempt / maxAttempts) * 30);
      setGenerationProgress(progress);

      const { imageUrl } = await fetchTaskStatus(taskId);
      if (imageUrl) {
        return imageUrl;
      }

      if (attempt < maxAttempts) {
        await new Promise((resolve) => setTimeout(resolve, delayMs));
      }
    }

    throw new Error(intl.formatMessage({ id: "app.aiGeneration.errTimeout" }));
  };

  const handleGenerate = async () => {
    if (!companyId) {
      setErrorMessage(intl.formatMessage({ id: "app.aiGeneration.errCompanyRequired" }));
      return;
    }
    if (!editDescription.trim() || uploadedImages.length === 0) {
      setErrorMessage(intl.formatMessage({ id: "app.aiGeneration.errImageAndPromptRequired" }));
      return;
    }

    setIsGenerating(true);
    setGenerationProgress(0);
    setErrorMessage(null);
    setCanCheckStatus(false);
    setCurrentTaskId(null);

    try {
      setGenerationProgress(10);
      const prompt = editDescription.trim();
      const formData = new FormData();
      formData.append("prompt", prompt);
      formData.append("ecoMode", "false");
      uploadedImages.forEach((image) => {
        formData.append("images", image.file);
      });

      if (apiMode === "pro") {
        formData.append("resolution", resolution);
        formData.append("aspectRatio", aspectRatio);
      } else {
        formData.append(
          "type",
          uploadedImages.length > 0 ? "IMAGETOIAMGE" : "TEXTTOIAMGE"
        );
        formData.append("numImages", String(numImages));
        formData.append("imageSize", imageSize);
        if (watermark.trim()) {
          formData.append("watermark", watermark.trim());
        }
      }
      if (callBackUrl.trim()) {
        formData.append("callBackUrl", callBackUrl.trim());
      }

      setGenerationProgress(50);
      const endpoint =
        apiMode === "pro" ? GENERATE_ENDPOINT : GENERATE_OR_EDIT_ENDPOINT;
      const { data } = await socialApiClient().post(endpoint, formData, {
        headers: { "Content-Type": "multipart/form-data" },
        params: { companyId },
      });

      setGenerationProgress(70);
      const providerCode = data?.payload?.raw?.code;
      const providerMessage = (data?.payload?.raw?.msg || "").toString();
      const insufficientCredits =
        providerCode === 402 ||
        providerMessage.toLowerCase().includes("insufficient");
      if (insufficientCredits) {
        throw new Error(intl.formatMessage({ id: "app.aiGeneration.errInsufficientCredits" }));
      }

      const taskId = data?.payload?.taskId || data?.data?.taskId || data?.taskId;
      if (!taskId) {
        throw new Error(intl.formatMessage({ id: "app.aiGeneration.errNoTaskId" }));
      }
      setCurrentTaskId(taskId);

      const imageUrl = await pollTaskStatus(taskId);

      setGenerationProgress(100);
      setCanCheckStatus(false);
      setCurrentTaskId(null);

      const generatedImage: GeneratedImage = {
        id: `${Date.now()}`,
        url: imageUrl,
        prompt: editDescription,
        timestamp: Date.now(),
      };

      setGeneratedImages((prev) => [generatedImage, ...prev]);
      window.dispatchEvent(new Event(WEMBII_AI_CREDITS_REFRESH_EVENT));
    } catch (error: any) {
      console.error("Error completo:", error);
      console.error("Stack:", error.stack);

      const responseMessage = error?.response?.data?.message;
      const parsedResponseMessage = Array.isArray(responseMessage)
        ? responseMessage.join(" ")
        : responseMessage;
      const errorMsg =
        parsedResponseMessage ||
        error?.response?.data?.error?.message ||
        error?.message ||
        intl.formatMessage({ id: "app.aiGeneration.errUnknown" });
      setErrorMessage(errorMsg);

      const isTimeout =
        typeof errorMsg === "string" &&
        errorMsg.toLowerCase().includes("tiempo de espera agotado");
      if (isTimeout && currentTaskId) {
        setCanCheckStatus(true);
      } else {
        setCanCheckStatus(false);
        setCurrentTaskId(null);
      }
    } finally {
      setIsGenerating(false);
      setGenerationProgress(0);
    }
  };

  const handleCheckStatus = async () => {
    if (!currentTaskId) return;
    setIsGenerating(true);
    setErrorMessage(null);
    try {
      const imageUrl = await pollTaskStatus(currentTaskId);
      const generatedImage: GeneratedImage = {
        id: `${Date.now()}`,
        url: imageUrl,
        prompt: editDescription,
        timestamp: Date.now(),
      };
      setGeneratedImages((prev) => [generatedImage, ...prev]);
      window.dispatchEvent(new Event(WEMBII_AI_CREDITS_REFRESH_EVENT));
      setCanCheckStatus(false);
      setCurrentTaskId(null);
    } catch (error: any) {
      setErrorMessage(error.message || intl.formatMessage({ id: "app.aiGeneration.errCheckStatus" }));
    } finally {
      setIsGenerating(false);
      setGenerationProgress(0);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if ((e.metaKey || e.ctrlKey) && e.key === "Enter") {
      handleGenerate();
    }
  };

  return (
    <Layout>
      <Header2
        className="ai-module-header"
        title={intl.formatMessage({ id: "app.sidebar.aiEditorImages" })}
      />
      <Content>
        <div className="ai-generation-container" style={styles.container}>
          {/* Panel Izquierdo - Controles */}
          <div
            className="ai-generation-left-panel ai-generation-left-panel--compact"
            style={styles.leftPanel}
          >
            {/* Sección de Subida de Imágenes */}
          <div style={styles.section}>
              {uploadedImages.length > 0 && (
                <h3 style={styles.sectionTitle}>
                  {intl.formatMessage({ id: "app.aiGeneration.sectionUploadTitle" })}
                </h3>
              )}
              {uploadedImages.length === 0 ? (
                <div
                  className="ai-upload-card"
                  style={styles.uploadCard}
                  onDragOver={handleDragOver}
                  onDrop={handleDrop}
                  onClick={() => fileInputRef.current?.click()}
                >
                  <div style={styles.uploadIcon}>
                    <FontAwesomeIcon icon={faUpload} size="2x" />
                  </div>
                  <h4 style={styles.uploadTitle}>{intl.formatMessage({ id: "app.aiGeneration.uploadTitle" })}</h4>
                  <p style={styles.uploadText}>
                    {intl.formatMessage({ id: "app.aiGeneration.uploadText" }, { maxImages })}{" "}
                    <span style={styles.uploadInfoInline}>
                      {intl.formatMessage({ id: "app.aiGeneration.uploadInfo" })}
                    </span>
                  </p>
                  <div
                    className="ai-upload-actions-row"
                    style={styles.uploadActionsRow}
                  >
                    <button type="button" style={styles.chooseButton}>
                      {intl.formatMessage({ id: "app.aiGeneration.chooseButton" })}
                    </button>
                    <button
                      type="button"
                      className="ai-gen-outline-btn"
                      style={styles.galleryButton}
                      onClick={(e) => {
                        e.stopPropagation();
                        setShowImageBankModal(true);
                      }}
                    >
                      <FontAwesomeIcon icon={faImages} />
                      {intl.formatMessage({ id: "app.socialMediaPost.imageBank" })}
                    </button>
                  </div>
                  <button
                    type="button"
                    className="d-flex align-items-center justify-content-center w-100 d-lg-none ai-gen-outline-btn ai-gen-outline-btn--success"
                    style={styles.takePhotoButton}
                    onClick={(e) => {
                      e.stopPropagation();
                      cameraInputRef.current?.click();
                    }}
                  >
                    <FontAwesomeIcon icon={faCamera} />
                    {intl.formatMessage({ id: "app.aiGeneration.takePhotoBtn" })}
                  </button>
                </div>
              ) : (
                <div style={styles.imagesContainer}>
                  <div style={styles.imagesGrid}>
                    {uploadedImages.map((img, index) => (
                      <div key={img.id} style={styles.imageThumbnail}>
                        <img
                          src={img.preview}
                          alt={`Referencia ${index + 1}`}
                          style={styles.thumbnailImage}
                        />
                        <div style={styles.imageBadge}>{index + 1}</div>
                        <button
                          style={styles.removeButton}
                          onClick={() => removeImage(img.id)}
                          title="Eliminar imagen"
                        >
                          <FontAwesomeIcon icon={faTimes} />
                        </button>
                      </div>
                    ))}
                    {uploadedImages.length < maxImages && (
                      <div
                        className="ai-add-image-slot"
                        style={styles.addImagePlaceholder}
                        onClick={() => fileInputRef.current?.click()}
                      >
                        <div style={styles.plusIcon}>+</div>
                        <div className="ai-left-muted" style={styles.remainingText}>
                          {intl.formatMessage({ id: "app.aiGeneration.remaining" }, { count: maxImages - uploadedImages.length })}
                        </div>
                      </div>
                    )}
                  </div>
                  <div
                    className="ai-left-muted ai-left-selection-info"
                    style={styles.selectionInfo}
                  >
                    {intl.formatMessage({ id: "app.aiGeneration.selectionInfo" }, { count: uploadedImages.length, total: maxImages })}
                  </div>
                  {uploadedImages.length < maxImages && (
                    <button
                      type="button"
                      className="ai-gen-outline-btn"
                      style={styles.galleryButtonFullWidth}
                      onClick={() => setShowImageBankModal(true)}
                    >
                      <FontAwesomeIcon icon={faImages} />
                      {intl.formatMessage({ id: "app.socialMediaPost.imageBank" })}
                    </button>
                  )}
                  {uploadedImages.length < maxImages && (
                    <button
                      type="button"
                      className="d-flex align-items-center justify-content-center w-100 d-lg-none ai-gen-outline-btn ai-gen-outline-btn--success"
                      style={styles.takePhotoButton}
                      onClick={() => cameraInputRef.current?.click()}
                    >
                      <FontAwesomeIcon icon={faCamera} />
                      {intl.formatMessage({ id: "app.aiGeneration.takePhotoBtn" })}
                    </button>
                  )}
                </div>
              )}

              <p className="ai-left-muted" style={styles.infoText}>
                {intl.formatMessage({ id: "app.aiGeneration.infoText" })}
              </p>

              <input
                ref={fileInputRef}
                type="file"
                accept="image/jpeg,image/jpg,image/png,image/webp"
                multiple
                onChange={handleFileInputChange}
                style={{ display: "none" }}
              />
              {/* Cámara (móvil): mismo flujo que subir archivo; el botón solo se muestra &lt; lg */}
              <input
                ref={cameraInputRef}
                type="file"
                accept="image/*"
                capture="environment"
                onChange={handleFileInputChange}
                style={{ display: "none" }}
              />
            </div>

            {/* Configuración NanoBanana (API selector) temporalmente oculto
            <div style={styles.section}>
              <h3 style={styles.sectionTitle}>{intl.formatMessage({ id: "app.aiGeneration.sectionConfigTitle" })}</h3>
              <div style={styles.selectRow}>
                <span className="selectLabel" style={styles.selectLabel}>{intl.formatMessage({ id: "app.aiGeneration.apiLabel" })}</span>
                <select
                  className="ai-select"
                  style={styles.selectInput}
                  value={apiMode}
                  onChange={(e) => setApiMode(e.target.value as "pro" | "edit")}
                >
                  <option value="pro">{intl.formatMessage({ id: "app.aiGeneration.apiOptionPro" })}</option>
                  <option value="edit">{intl.formatMessage({ id: "app.aiGeneration.apiOptionEdit" })}</option>
                </select>
              </div>
            </div>
            */}

            {/* Sección de Descripción */}
            <div style={styles.section}>
              <h3 style={styles.sectionTitle}>{intl.formatMessage({ id: "app.aiGeneration.sectionDescTitle" })}</h3>
              <textarea
                className="ai-textarea"
                style={styles.textArea}
                placeholder={intl.formatMessage({ id: "app.aiGeneration.placeholder" })}
                value={editDescription}
                onChange={(e) => setEditDescription(e.target.value)}
                onKeyDown={handleKeyPress}
                rows={4}
              />
              <div className="ai-left-muted" style={styles.shortcutHint}>
                <span className="ai-kbd" style={styles.shortcutKey}>
                  ⌘
                </span>
                <span style={styles.shortcutPlus}>+</span>
                <span className="ai-kbd" style={styles.shortcutKey}>
                  Enter
                </span>
              </div>
            </div>

            {/* Mismo criterio de tamaño que Text-to-image (generate-or-edit) */}
            <div style={styles.sectionCompact}>
              <div
                className="ai-gen-options-compact"
                style={styles.optionsRowCompact}
              >
                <span
                  className="ai-options-section-title"
                  style={styles.sectionSubtitleInline}
                >
                  {intl.formatMessage({ id: "app.textToImage.sectionOptionsTitle" })}
                </span>
                <label
                  className="ai-options-size-label"
                  style={styles.sizeLabelCompact}
                  htmlFor="ai-gen-image-size"
                >
                  {intl.formatMessage({ id: "app.textToImage.imageSize" })}
                </label>
                <select
                  id="ai-gen-image-size"
                  className="ai-select"
                  style={styles.selectInputCompact}
                  value={apiMode === "pro" ? aspectRatio : imageSize}
                  onChange={(e) => {
                    const v = e.target.value;
                    setImageSize(v);
                    setAspectRatio(v);
                  }}
                  aria-label={intl.formatMessage({
                    id: "app.textToImage.imageSize",
                  })}
                >
                  <option value="4:5">4:5</option>
                  <option value="1:1">1:1</option>
                  <option value="16:9">16:9</option>
                  <option value="9:16">9:16</option>
                </select>
              </div>
            </div>

            {/* Mensaje de Error */}
            {errorMessage && (
              <div className="ai-gen-alert ai-gen-alert--error" style={styles.errorMessage}>
                {errorMessage}
              </div>
            )}

            {canCheckStatus && currentTaskId && !isGenerating && (
              <>
                <button
                  type="button"
                  className="ai-gen-secondary-btn"
                  style={styles.secondaryButton}
                  onClick={handleCheckStatus}
                >
                  {intl.formatMessage({ id: "app.aiGeneration.checkStatusBtn" })}
                </button>
                <div className="ai-left-muted" style={styles.helperText}>
                  {intl.formatMessage({ id: "app.aiGeneration.checkStatusHelper" })}
                </div>
              </>
            )}

            {/* Botón de Generar */}
            <button
              type="button"
              className="ai-gen-generate-btn"
              style={{
                ...styles.generateButton,
                ...(isGenerating || !editDescription.trim() || uploadedImages.length === 0
                  ? styles.generateButtonDisabled
                  : {}),
              }}
              onClick={handleGenerate}
              disabled={isGenerating || !editDescription.trim() || uploadedImages.length === 0}
            >
              {isGenerating ? (
                <>
                  <FontAwesomeIcon icon={faSpinner} spin style={{ marginRight: "8px" }} />
                  {intl.formatMessage({ id: "app.aiGeneration.generatingBtn" })}
                </>
              ) : (
                <>
                  <FontAwesomeIcon icon={faMagic} style={{ marginRight: "8px" }} />
                  {intl.formatMessage({ id: "app.aiGeneration.generateBtn" })}
                </>
              )}
            </button>
          </div>

          {/* Panel Derecho - Resultados */}
          <div
            className={cn(
              "ai-generation-right-panel",
              generatedImages.length === 0 &&
                !isGenerating &&
                "ai-generation-right-panel--empty-guide"
            )}
            style={styles.rightPanel}
          >
        {generatedImages.length > 0 ? (
          <>
            <div className="ai-results-toolbar">
              <label
                htmlFor="ai-results-cols-aigen"
                className="ai-results-toolbar__label"
              >
                {intl.formatMessage({
                  id: "app.aiGeneration.resultsGridColumnsLabel",
                })}
              </label>
              <select
                id="ai-results-cols-aigen"
                className="ai-select ai-results-toolbar__select"
                value={resultColumns}
                onChange={(e) =>
                  setResultColumns(Number(e.target.value) as AiResultsColumns)
                }
                aria-label={intl.formatMessage({
                  id: "app.aiGeneration.resultsGridColumnsAria",
                })}
              >
                {([1, 2, 3, 4] as const).map((n) => (
                  <option key={n} value={n}>
                    {n}
                  </option>
                ))}
              </select>
            </div>
            <div
              className={`ai-results-grid ai-results-grid--cols-${resultColumns}`}
            >
            {isGenerating && (
              <div className="ai-loading-card" style={styles.loadingCard}>
                <div style={styles.loadingSpinner}>
                  <FontAwesomeIcon icon={faSpinner} spin size="2x" />
                </div>
                <p style={styles.loadingText}>{intl.formatMessage({ id: "app.aiGeneration.generatingBtn" })}</p>
                {generationProgress > 0 && (
                  <div className="ai-gen-progress-track" style={styles.progressBar}>
                    <div
                      style={{
                        ...styles.progressFill,
                        width: `${generationProgress}%`,
                      }}
                    />
                  </div>
                )}
                <p style={styles.progressText}>{generationProgress}%</p>
              </div>
            )}

            {generatedImages.map((img) => {
              const deg = imageRotations[img.id] ?? 0;
              return (
              <div key={img.id} className="ai-result-card" style={styles.resultCard}>
                <div
                  className="ai-result-image-wrap"
                  style={styles.resultImageWrapper}
                >
                  <img
                    src={img.url}
                    alt="Imagen generada"
                    className="ai-result-image"
                    style={{
                      ...styles.resultImage,
                      transform: `rotate(${deg}deg)`,
                      transition: "transform 0.25s ease",
                    }}
                    onError={(e) => {
                      e.currentTarget.src = `https://picsum.photos/512/512?random=${Date.now()}`;
                    }}
                  />
                </div>
                <div className="ai-result-prompt" style={styles.resultPrompt}>{img.prompt}</div>
                <div className="ai-result-actions" style={styles.resultActions}>
                  <button
                    className="ai-result-action-btn"
                    style={styles.resultActionButton}
                    type="button"
                    onClick={() => openViewerForImage(img)}
                    disabled={viewerPreparingId === img.id || viewerApplying}
                    title={intl.formatMessage({
                      id: "app.aiGeneration.viewerOpenBtnTitle",
                    })}
                  >
                    <FontAwesomeIcon
                      icon={viewerPreparingId === img.id ? faSpinner : faEye}
                      spin={viewerPreparingId === img.id}
                    />{" "}
                    {intl.formatMessage({ id: "app.aiGeneration.viewerOpenBtn" })}
                  </button>
                  <button
                    className="ai-result-action-btn"
                    style={styles.resultActionButton}
                    type="button"
                    onClick={() => rotateGeneratedImage(img.id)}
                  >
                    <FontAwesomeIcon icon={faRotateRight} /> {intl.formatMessage({ id: "app.aiGeneration.rotateBtn" })}
                  </button>
                  <button
                    className="ai-result-action-btn"
                    style={styles.resultActionButton}
                    type="button"
                    onClick={() => openCropModalForImage(img)}
                    disabled={cropPreparingId === img.id || cropApplying}
                  >
                    <FontAwesomeIcon
                      icon={cropPreparingId === img.id ? faSpinner : faCrop}
                      spin={cropPreparingId === img.id}
                    />{" "}
                    {intl.formatMessage({ id: "app.aiGeneration.cropBtn" })}
                  </button>
                  <button
                    className="ai-result-action-btn"
                    style={styles.resultActionButton}
                    type="button"
                    onClick={() => downloadGeneratedImage(img)}
                  >
                    <FontAwesomeIcon icon={faDownload} /> {intl.formatMessage({ id: "app.aiGeneration.downloadBtn" })}
                  </button>
                  <button
                    className="ai-result-action-btn"
                    style={styles.resultActionButton}
                    type="button"
                    onClick={() => addGeneratedAsReference(img)}
                  >
                    <FontAwesomeIcon icon={faPlus} /> {intl.formatMessage({ id: "app.aiGeneration.useAsRefBtn" })}
                  </button>
                  <button
                    className="ai-result-action-danger-btn"
                    style={styles.resultActionDangerButton}
                    type="button"
                    onClick={() => removeGeneratedImage(img.id)}
                  >
                    <FontAwesomeIcon icon={faTimes} /> {intl.formatMessage({ id: "app.aiGeneration.deleteBtn" })}
                  </button>
                </div>
                <div className="ai-result-post-wrap">
                  <button
                    type="button"
                    className="ai-result-post-btn"
                    disabled={postingToSocialId === img.id}
                    onClick={() => postImageToSocial(img)}
                  >
                    {postingToSocialId === img.id ? (
                      <>
                        <FontAwesomeIcon icon={faSpinner} spin />{" "}
                        <span>
                          {intl.formatMessage({
                            id: "app.aiGeneration.postToSocialLoading",
                          })}
                        </span>
                      </>
                    ) : (
                      <>
                        <FontAwesomeIcon icon={faPaperPlane} />{" "}
                        <span>
                          {intl.formatMessage({
                            id: "app.aiGeneration.postToSocialBtn",
                          })}
                        </span>
                      </>
                    )}
                  </button>
                </div>
              </div>
            );
            })}
            </div>
          </>
        ) : isGenerating ? (
          <div className="ai-gen-loading-container" style={styles.loadingContainer}>
            <div style={styles.loadingSpinner}>
              <FontAwesomeIcon icon={faSpinner} spin size="3x" />
            </div>
            <p style={styles.loadingText}>{intl.formatMessage({ id: "app.aiGeneration.generatingBtn" })}</p>
            {generationProgress > 0 && (
              <div className="ai-gen-progress-track" style={styles.progressBar}>
                <div
                  style={{
                    ...styles.progressFill,
                    width: `${generationProgress}%`,
                  }}
                />
              </div>
            )}
            <p style={styles.progressText}>{generationProgress}%</p>
          </div>
        ) : (
          <div className="text-to-image-empty">
            <div className="text-to-image-empty__icon-wrap" aria-hidden>
              <FontAwesomeIcon icon={faMagic} size="2x" />
            </div>
            <h2 className="text-to-image-empty__title">
              {intl.formatMessage({ id: "app.aiGeneration.emptyTitle" })}
            </h2>
            <p className="text-to-image-empty__intro">
              {intl.formatMessage({ id: "app.aiGeneration.emptyIntro" })}
            </p>
            <ol className="text-to-image-empty__steps">
              {AI_GENERATION_EMPTY_STEPS.map((stepId, index) => (
                <li key={stepId} className="text-to-image-empty__step">
                  <span className="text-to-image-empty__step-num">
                    {index + 1}
                  </span>
                  <span>{intl.formatMessage({ id: stepId })}</span>
                </li>
              ))}
            </ol>
            <p className="text-to-image-empty__tip" role="note">
              <FontAwesomeIcon
                icon={faLightbulb}
                className="text-to-image-empty__tip-icon"
                aria-hidden
              />
              {intl.formatMessage({ id: "app.aiGeneration.emptyTip" })}
            </p>
          </div>
        )}
      </div>
        </div>
      </Content>
      <AiImageCropModal
        isOpen={!!cropModal}
        imageSrc={cropModal?.src ?? null}
        onClose={closeCropModal}
        onApply={handleCropApply}
        busy={cropApplying}
      />
      <AiImageViewerEditorModal
        isOpen={!!viewerModal}
        imageSrc={viewerModal?.src ?? null}
        onClose={closeViewerModal}
        onApply={handleViewerApply}
        busy={viewerApplying}
      />
      <ImageBankModal
        show={showImageBankModal}
        onHide={() => setShowImageBankModal(false)}
        onImageSelect={handleGalleryImagesSelect}
      />
      <ProductGuideModal
        isOpen={showAiGuide}
        onClose={closeAiGuide}
        modalTitleId="app.aiGeneration.guide.modalTitle"
        steps={AI_GUIDE_STEPS}
        persistDismissKey="ai"
      />
    </Layout>
  );
}

// Estilos inline con colores de Wembii
const styles: { [key: string]: React.CSSProperties } = {
  container: {
    display: "flex",
    height: "calc(100vh - 110px)",
    gap: "20px",
    padding: "16px 20px",
    color: "#41463d",
    minWidth: 0,
    maxWidth: "100%",
    boxSizing: "border-box",
  },
  leftPanel: {
    display: "flex",
    flexDirection: "column",
    gap: "12px",
    overflowY: "auto",
    paddingRight: "8px",
  },
  rightPanel: {
    flex: 1,
    minWidth: 0,
    borderRadius: "30px",
    padding: "24px",
    overflowY: "auto",
    display: "flex",
    flexDirection: "column",
    alignItems: "stretch",
    justifyContent: "flex-start",
    border: "3px solid #e6e6ee",
  },
  section: {
    marginBottom: "4px",
  },
  sectionCompact: {
    marginBottom: "0",
    marginTop: "2px",
  },
  sectionTitle: {
    fontSize: "15px",
    fontWeight: 600,
    marginBottom: "8px",
    color: "#0B65C2",
  },
  sectionSubtitle: {
    fontSize: "14px",
    fontWeight: 600,
    marginBottom: "12px",
    color: "#41463d",
  },
  sectionSubtitleInline: {
    fontSize: "12px",
    fontWeight: 600,
    color: "#41463d",
    marginRight: "4px",
    whiteSpace: "nowrap",
  },
  optionsRow: {
    display: "flex",
    alignItems: "center",
    gap: "12px",
    marginBottom: "12px",
  },
  optionsRowCompact: {
    display: "flex",
    flexWrap: "wrap",
    alignItems: "center",
    gap: "8px 10px",
    padding: "8px 10px",
    backgroundColor: "#f8f9fb",
    borderRadius: "12px",
    border: "1px solid #e6e6ee",
    boxSizing: "border-box",
  },
  sizeLabel: {
    fontSize: "13px",
    color: "#41463d",
    minWidth: "80px",
  },
  sizeLabelCompact: {
    fontSize: "12px",
    color: "#6c757d",
    fontWeight: 500,
    margin: 0,
    whiteSpace: "nowrap",
  },
  uploadCard: {
    backgroundColor: "#ffffff",
    borderRadius: "16px",
    padding: "10px 12px 12px",
    textAlign: "center",
    cursor: "pointer",
    border: "2px dashed #d8dce6",
    transition: "all 0.2s ease",
  },
  uploadIcon: {
    color: "#0B65C2",
    marginBottom: "6px",
  },
  uploadTitle: {
    fontSize: "14px",
    fontWeight: 600,
    marginBottom: "4px",
    color: "#41463d",
    lineHeight: 1.25,
  },
  uploadText: {
    fontSize: "11px",
    color: "#6c757d",
    marginBottom: "8px",
    lineHeight: 1.4,
  },
  uploadInfoInline: {
    fontSize: "10px",
    color: "#868e96",
  },
  uploadActionsRow: {
    display: "flex",
    flexWrap: "wrap",
    gap: "8px",
    width: "100%",
    boxSizing: "border-box",
  },
  chooseButton: {
    backgroundColor: "#0B65C2",
    color: "#ffffff",
    border: "none",
    borderRadius: "12px",
    padding: "8px 12px",
    fontSize: "13px",
    fontWeight: 600,
    cursor: "pointer",
    transition: "all 0.2s ease",
    flex: "1 1 120px",
    minWidth: 0,
    boxSizing: "border-box",
  },
  galleryButton: {
    marginTop: 0,
    flex: "1 1 120px",
    minWidth: 0,
    backgroundColor: "#ffffff",
    color: "#0B65C2",
    border: "2px solid #0B65C2",
    borderRadius: "12px",
    padding: "7px 10px",
    fontSize: "12px",
    fontWeight: 600,
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "6px",
    boxSizing: "border-box",
  },
  galleryButtonFullWidth: {
    marginTop: "8px",
    width: "100%",
    backgroundColor: "#ffffff",
    color: "#0B65C2",
    border: "2px solid #0B65C2",
    borderRadius: "12px",
    padding: "8px 12px",
    fontSize: "13px",
    fontWeight: 600,
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "8px",
    boxSizing: "border-box",
  },
  takePhotoButton: {
    marginTop: "8px",
    width: "100%",
    backgroundColor: "#ffffff",
    color: "#198754",
    border: "2px solid #198754",
    borderRadius: "12px",
    padding: "8px 12px",
    fontSize: "12px",
    fontWeight: 600,
    cursor: "pointer",
    gap: "6px",
    boxSizing: "border-box",
  },
  imagesContainer: {
    marginBottom: "12px",
  },
  imagesGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fill, minmax(min(100%, 96px), 1fr))",
    gap: "12px",
    marginBottom: "12px",
    minWidth: 0,
  },
  imageThumbnail: {
    position: "relative",
    aspectRatio: "1",
    borderRadius: "15px",
    overflow: "hidden",
    backgroundColor: "#f8f9fa",
    border: "1px solid #e6e6ee",
  },
  thumbnailImage: {
    width: "100%",
    height: "100%",
    objectFit: "cover",
  },
  imageBadge: {
    position: "absolute",
    bottom: "8px",
    left: "8px",
    backgroundColor: "#0B65C2",
    color: "#ffffff",
    borderRadius: "50%",
    width: "24px",
    height: "24px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: "12px",
    fontWeight: 600,
  },
  removeButton: {
    position: "absolute",
    top: "4px",
    right: "4px",
    backgroundColor: "rgba(210, 42, 48, 0.9)",
    color: "#ffffff",
    border: "none",
    borderRadius: "50%",
    width: "24px",
    height: "24px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    cursor: "pointer",
    fontSize: "12px",
  },
  addImagePlaceholder: {
    aspectRatio: "1",
    border: "3px dashed #e6e6ee",
    borderRadius: "15px",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    cursor: "pointer",
    backgroundColor: "#f8f9fa",
    transition: "all 0.2s ease",
  },
  plusIcon: {
    fontSize: "24px",
    color: "#0B65C2",
    marginBottom: "4px",
  },
  remainingText: {
    fontSize: "12px",
    color: "#6c757d",
  },
  selectionInfo: {
    fontSize: "12px",
    color: "#6c757d",
    textAlign: "center",
    padding: "8px",
    backgroundColor: "#f8f9fa",
    borderRadius: "15px",
    border: "1px solid #e6e6ee",
  },
  infoText: {
    fontSize: "11px",
    color: "#6c757d",
    marginTop: "6px",
    lineHeight: 1.4,
  },
  textArea: {
    width: "100%",
    maxWidth: "100%",
    boxSizing: "border-box",
    backgroundColor: "#ffffff",
    border: "1px solid #e6e6ee",
    borderRadius: "12px",
    padding: "10px 12px",
    color: "#41463d",
    fontSize: "13px",
    fontFamily: "inherit",
    resize: "vertical",
    minHeight: "120px",
  },
  modelInput: {
    width: "100%",
    backgroundColor: "#ffffff",
    border: "1px solid #e6e6ee",
    borderRadius: "12px",
    padding: "12px 14px",
    color: "#41463d",
    fontSize: "13px",
    fontFamily: "inherit",
  },
  checkboxRow: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
    marginBottom: "8px",
    fontSize: "12px",
    color: "#41463d",
  },
  checkboxLabel: {
    fontSize: "12px",
    color: "#41463d",
  },
  selectRow: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
    marginBottom: "8px",
  },
  selectLabel: {
    fontSize: "12px",
    color: "#41463d",
  },
  selectInput: {
    flex: 1,
    backgroundColor: "#ffffff",
    border: "1px solid #e6e6ee",
    borderRadius: "10px",
    padding: "8px 10px",
    color: "#41463d",
    fontSize: "12px",
    fontFamily: "inherit",
  },
  selectInputCompact: {
    minWidth: "88px",
    maxWidth: "120px",
    flex: "0 0 auto",
    backgroundColor: "#ffffff",
    border: "1px solid #e6e6ee",
    borderRadius: "8px",
    padding: "5px 8px",
    color: "#41463d",
    fontSize: "12px",
    fontFamily: "inherit",
  },
  shortcutHint: {
    display: "flex",
    alignItems: "center",
    gap: "4px",
    marginTop: "4px",
    justifyContent: "flex-end",
    fontSize: "10px",
    color: "#6c757d",
  },
  shortcutKey: {
    backgroundColor: "#f8f9fa",
    border: "1px solid #e6e6ee",
    borderRadius: "4px",
    padding: "2px 6px",
    fontSize: "10px",
    color: "#41463d",
  },
  shortcutPlus: {
    color: "#6c757d",
  },
  errorMessage: {
    backgroundColor: "#fff3cd",
    border: "1px solid #ffc107",
    borderRadius: "15px",
    padding: "12px",
    color: "#856404",
    fontSize: "14px",
    marginBottom: "8px",
  },
  statusMessage: {
    backgroundColor: "#e6f0ff",
    border: "1px solid #a9c7ff",
    borderRadius: "15px",
    padding: "10px 12px",
    color: "#1f4ea3",
    fontSize: "13px",
    marginBottom: "8px",
  },
  secondaryButton: {
    width: "100%",
    backgroundColor: "#ffffff",
    color: "#0B65C2",
    border: "1px solid #0B65C2",
    borderRadius: "25px",
    padding: "12px 20px",
    fontSize: "14px",
    fontWeight: 600,
    cursor: "pointer",
    marginBottom: "8px",
  },
  generateButton: {
    width: "100%",
    backgroundColor: "#0B65C2",
    color: "#ffffff",
    border: "none",
    borderRadius: "14px",
    padding: "11px 16px",
    fontSize: "14px",
    fontWeight: 600,
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    transition: "all 0.2s ease",
    marginTop: "10px",
  },
  generateButtonDisabled: {
    backgroundColor: "#c4c4c4",
    color: "#6c757d",
    cursor: "not-allowed",
    opacity: 0.6,
  },
  loadingContainer: {
    width: "100%",
    minHeight: "280px",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    gap: "16px",
    alignSelf: "stretch",
  },
  loadingSpinner: {
    color: "#0B65C2",
  },
  loadingText: {
    fontSize: "18px",
    color: "#41463d",
    fontWeight: 500,
  },
  progressBar: {
    width: "100%",
    maxWidth: "300px",
    height: "4px",
    backgroundColor: "#e6e6ee",
    borderRadius: "2px",
    overflow: "hidden",
  },
  progressFill: {
    height: "100%",
    backgroundColor: "#0B65C2",
    transition: "width 0.3s ease",
  },
  progressText: {
    fontSize: "14px",
    color: "#6c757d",
    fontWeight: 500,
  },
  helperText: {
    fontSize: "11px",
    color: "#6c757d",
    textAlign: "center",
    marginTop: "4px",
    marginBottom: "4px",
  },
  loadingCard: {
    backgroundColor: "#ffffff",
    borderRadius: "20px",
    border: "2px dashed #0B65C2",
    padding: "16px 12px",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    gap: "8px",
  },
  resultCard: {
    borderRadius: "20px",
    overflow: "hidden",
    display: "flex",
    flexDirection: "column",
    backgroundColor: "#ffffff",
    boxShadow: "0 2px 12px rgba(0,0,0,0.06)",
    minWidth: 0,
    maxWidth: "100%",
  },
  resultImageWrapper: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    width: "100%",
    maxWidth: "100%",
    minWidth: 0,
    minHeight: 0,
    overflow: "hidden",
    padding: "0 4px",
    boxSizing: "border-box",
  },
  resultImage: {
    maxWidth: "100%",
    width: "100%",
    height: "auto",
    maxHeight: "min(85vh, 1200px)",
    objectFit: "contain",
    display: "block",
  },
  resultPrompt: {
    padding: "12px",
    fontSize: "12px",
    minHeight: "48px",
  },
  resultActions: {
    display: "flex",
    flexDirection: "column",
    flexWrap: "nowrap",
    alignItems: "stretch",
    gap: "8px",
    padding: "10px 12px 12px",
  },
  resultActionButton: {
    flex: "none",
    width: "100%",
    borderRadius: "10px",
    padding: "10px 10px",
    fontSize: "12px",
    fontWeight: 600,
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "6px",
    boxSizing: "border-box",
  },
  resultActionDangerButton: {
    flex: "none",
    width: "100%",
    borderRadius: "10px",
    padding: "10px 10px",
    fontSize: "12px",
    fontWeight: 600,
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "6px",
    boxSizing: "border-box",
  },
};
